import {
  type SearchSourceId,
  type SearchSourceState,
} from "@/features/map/utils/buildSearchChain";

export interface BuildSearchSourceStatesParams {
  /** GEOSPACE の地図契約があるか。GEOSPACE 系 2 ソースの可否を決める。 */
  hasGeospaceContract: boolean;
  /** ゼンリンの地図契約があるか。 */
  hasZenrinContract: boolean;
  /** 役所地番地図のフィーチャーフラグ。 */
  trustartChibanMap: boolean;
  /**
   * 役所地番地図・登記所備付地図を利用できるユーザーか（BE: is_municipal_parcel_map_available）。
   * BE の kill-switch が無効な間は全ユーザー、有効化後は契約テナントのみ true になる。
   * BE の検索 API も同じ判定で 403 を返すため、false のときは候補に入れない。
   */
  isMunicipalParcelMapAvailable: boolean;
  /** 登記所備付地図のフィーチャーフラグ。 */
  registrationMapLayer: boolean;
  /** 各データソースに対応するレイヤーを表示しているか。 */
  visible: Record<SearchSourceId, boolean>;
}

/**
 * 各データソースの「使えるか（available）」と「表示中か（visible）」を組み立てる純粋関数。
 *
 * available は契約・フィーチャーフラグ・利用可否だけで決まる。登記所備付のタイル配信
 * （pmtiles）が取れているかは表示の話であって検索 API の可否とは独立なので、ここでは見ない。
 * クリック・登記地番・住居表示・範囲選択の 4 検索がこの結果から作ったチェーンを使うため、
 * ソースを叩く条件はここだけで決める。
 */
export const buildSearchSourceStates = ({
  hasGeospaceContract,
  hasZenrinContract,
  trustartChibanMap,
  isMunicipalParcelMapAvailable,
  registrationMapLayer,
  visible,
}: BuildSearchSourceStatesParams): Record<
  SearchSourceId,
  SearchSourceState
> => ({
  geospaceChiban: {
    available: hasGeospaceContract,
    visible: visible.geospaceChiban,
  },
  geospaceLegalAffairs: {
    available: hasGeospaceContract,
    visible: visible.geospaceLegalAffairs,
  },
  trustartChiban: {
    available: trustartChibanMap && isMunicipalParcelMapAvailable,
    visible: visible.trustartChiban,
  },
  registrationMap: {
    available: registrationMapLayer && isMunicipalParcelMapAvailable,
    visible: visible.registrationMap,
  },
  zenrin: {
    available: hasZenrinContract,
    visible: visible.zenrin,
  },
});
